import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { RefreshCw, CheckCircle2, XCircle, Clock, AlertTriangle, MinusCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  synced: { label: "Sincronizzato", cls: "bg-green-100 text-green-800", Icon: CheckCircle2 },
  partial: { label: "Parziale", cls: "bg-amber-100 text-amber-800", Icon: AlertTriangle },
  failed: { label: "Fallito", cls: "bg-red-100 text-red-800", Icon: XCircle },
  pending: { label: "In attesa", cls: "bg-slate-100 text-slate-700", Icon: Clock },
  skipped: { label: "Saltato", cls: "bg-slate-100 text-slate-500", Icon: MinusCircle },
};

const Badge = ({ status }) => {
  const s = STATUS[status] || STATUS.pending;
  const { Icon } = s;
  return (
    <span data-testid={`sync-badge-${status}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${s.cls}`}>
      <Icon className="w-3.5 h-3.5" /> {s.label}
    </span>
  );
};

export default function AdminPage() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/bookings`);
      setBookings(r.data.bookings || []);
    } catch (e) {
      console.error("Failed to load admin bookings:", e);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const retry = async (id) => {
    setRetrying(id);
    try {
      await axios.post(`${API}/admin/bookings/${id}/retry`);
      await load();
    } catch (e) { console.error("Retry Smoobu sync failed:", e); } finally { setRetrying(null); }
  };

  const counts = bookings.reduce((acc, b) => {
    const s = b.smoobu_sync_status || "pending";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <section data-testid="admin-page" className="pt-36 pb-20 min-h-screen bg-[#F2EFEB]">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="font-display text-4xl md:text-5xl text-[#1A1A1A] tracking-tight">Admin · Prenotazioni</h1>
            <p className="mt-2 text-sm text-[#5C5C5C]">Monitoraggio sincronizzazione Smoobu</p>
          </div>
          <button data-testid="admin-refresh" onClick={load}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1D3557] text-white text-sm font-medium hover:bg-[#345B73] transition-all">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Aggiorna
          </button>
        </div>

        <div className="flex flex-wrap gap-3 mb-6">
          <span className="px-3 py-1.5 rounded-xl bg-white text-sm text-[#1A1A1A]" data-testid="admin-total">Totale: <strong>{bookings.length}</strong></span>
          {Object.keys(STATUS).map((s) => counts[s] ? (
            <span key={s} className="px-3 py-1.5 rounded-xl bg-white text-sm"><Badge status={s} /> <strong className="ml-1">{counts[s]}</strong></span>
          ) : null)}
        </div>

        <div className="bg-white rounded-2xl border border-black/5 overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-bookings-table">
            <thead>
              <tr className="text-left text-[#5C5C5C] border-b border-black/5">
                <th className="px-4 py-3 font-medium">Ospite</th>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Camere</th>
                <th className="px-4 py-3 font-medium">Smoobu</th>
                <th className="px-4 py-3 font-medium">ID Smoobu</th>
                <th className="px-4 py-3 font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-[#5C5C5C]">Caricamento…</td></tr>
              )}
              {!loading && bookings.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-[#5C5C5C]" data-testid="admin-empty">Nessuna prenotazione.</td></tr>
              )}
              {!loading && bookings.map((b) => {
                const rooms = (b.rooms || []).join(", ") || b.room || "—";
                const ids = (b.smoobu_reservation_ids || []).filter(Boolean);
                const canRetry = ["failed", "partial", "pending"].includes(b.smoobu_sync_status);
                return (
                  <tr key={b.id} data-testid={`admin-row-${b.id}`} className="border-b border-black/5 last:border-0 align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#1A1A1A]">{b.first_name} {b.last_name}</div>
                      <div className="text-xs text-[#5C5C5C] break-all">{b.email}</div>
                      <div className="text-xs text-[#5C5C5C]">{b.phone}</div>
                    </td>
                    <td className="px-4 py-3 text-[#1A1A1A] whitespace-nowrap">{b.arrival} → {b.departure}<div className="text-xs text-[#5C5C5C]">{b.guests} ospiti</div></td>
                    <td className="px-4 py-3 text-[#1A1A1A]">{rooms}</td>
                    <td className="px-4 py-3">
                      <Badge status={b.smoobu_sync_status} />
                      {b.smoobu_sync_error && <div className="mt-1 text-xs text-red-700 max-w-[220px] break-words">{b.smoobu_sync_error}</div>}
                    </td>
                    <td className="px-4 py-3 text-[#1A1A1A]">{ids.length ? ids.join(", ") : "—"}</td>
                    <td className="px-4 py-3">
                      {canRetry && (
                        <button data-testid={`admin-retry-${b.id}`} onClick={() => retry(b.id)} disabled={retrying === b.id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#1D3557]/30 text-[#1D3557] text-xs font-medium hover:bg-[#1D3557]/5 transition-all disabled:opacity-50">
                          <RefreshCw className={`w-3.5 h-3.5 ${retrying === b.id ? "animate-spin" : ""}`} /> Ritenta
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
